import 'nprogress/nprogress.css';

import NProgress from 'nprogress';
import { createRouter, createWebHistory } from 'vue-router';
import { isBigolive, gotoLoginPage } from '@/common/utils';
import { AccountStatus } from '@/constants/enum';
import { useAppStore } from '@/store/app';
import authApi, { enrichAgentInfo } from '@/apis/auth';
import getIsBanLogin from '@/compositions/useLoginban';

import { RouteName } from './names';
import routes from './routes';
import { hasRoutePermission } from './utils';
import { catchError } from '@bigo/helpers';
import { liveAgentServiceGetPermissions } from '@/api';

NProgress.configure({ showSpinner: false });

const router = createRouter({
  history: createWebHistory(import.meta.env.VITE_BASE_URL),
  routes,
});

function allowAccessRoute(to) {
  const appStore = useAppStore();
  if (isBigolive && to.name === RouteName.Welcome) {
    return true;
  }

  // 允许访问登录页和无权限页
  if ([RouteName.Login, RouteName.NotFound].includes(to.name)) return true;

  // 判断是否未登录或账号被禁用
  if (!appStore.loginUser || appStore.loginUser.status === AccountStatus.Disabled) return false;

  // 判断是否管理员
  if (appStore.isAdmin) return true;
  // 这三个申请页面不用鉴权，或者细化鉴权，todo
  if (
    [
      RouteName.ApplyNoPermission,
      RouteName.ApplyForm,
      RouteName.ApplyProcess,
      RouteName.AgentContact,
      RouteName.AgentCertifyForm,
    ].includes(to.name)
  )
    return true;
  // 判断路由权限
  return to.matched.every((route) => hasRoutePermission(route));
}

const getUserInfo = async () => {
  const appStore = useAppStore();

  await catchError(appStore.getTransferPermission());
  // 81497 新增接口返回新权限，优先级比getUserInfo返回的要高
  const [permissionsErr, permissionData] = await catchError(
    liveAgentServiceGetPermissions({ permission_query_entry_type: 3 }),
  );
  const permissions = permissionData?.data?.permissions;
  const [err, userInfo] = await catchError(authApi.getUserInfo());
  if (!err && !permissionsErr) {
    if (userInfo.permissions && permissions) {
      userInfo.permissions = userInfo.permissions.concat(permissions);
    }
    // 如果是代理，获取运营后台的代理名称
    await enrichAgentInfo(userInfo);
    // 设置多语言
    appStore.setLoginUser(userInfo);

    return userInfo;
  }
  return Promise.reject();
};

router.beforeEach((to, from, next) => {
  NProgress.start();

  if (isBigolive && to.name === RouteName.Login) {
    next({ name: RouteName.Welcome });
  }
  if (allowAccessRoute(to)) {
    next();
  } else if (useAppStore().loginUser) {
    next({ name: RouteName.NotFound });
  } else {
    getUserInfo().then(
      () => {
        if (allowAccessRoute(to)) {
          next();
        } else {
          next({ name: RouteName.NotFound });
        }
      },
      () => {
        if (isBigolive) {
          next({ name: RouteName.Welcome });
          gotoLoginPage();
        } else {
          // 如果直接访问这些url，不做跳转
          if (getIsBanLogin()) return;
          next({ name: RouteName.Login });
        }
      },
    );
  }
});

router.afterEach(() => {
  NProgress.done();
});

export default router;
